import { useCallback } from 'react';

import type { SourceType } from '@trello/analytics-types';
import { Analytics } from '@trello/atlassian-analytics';
import { calculateItemPosition } from '@trello/position';

import { readListVisibleCardsFromCache } from 'app/src/components/List/readListVisibleCardsFromCache';
import type { BulkMoveCardsMutationVariables } from './BulkMoveCardsMutation.generated';
import { useBulkMoveCardsMutation } from './BulkMoveCardsMutation.generated';

export interface BulkMoveCardsArgs {
  cardIds: BulkMoveCardsMutationVariables['cardIds'];
  idBoard: BulkMoveCardsMutationVariables['idBoard'];
  posIndex: number;
  targetBoardId: BulkMoveCardsMutationVariables['targetBoardId'];
  targetListId: BulkMoveCardsMutationVariables['targetListId'];
  source?: SourceType;
}

export function useBulkCardMove() {
  const [bulkMoveCardsMutation] = useBulkMoveCardsMutation();

  const bulkMoveCards = useCallback(
    async ({
      cardIds,
      idBoard,
      posIndex,
      targetBoardId,
      targetListId,
      source,
    }: BulkMoveCardsArgs) => {
      let traceId: string | undefined;
      try {
        const cardsInList = readListVisibleCardsFromCache({
          boardId: targetBoardId,
          listId: targetListId,
        });

        const pos = calculateItemPosition(posIndex, cardsInList);

        traceId = Analytics.startTask({
          taskName: 'edit-card-bulk/idBoard',
          source: source || 'moveCardInlineDialog',
        });

        await bulkMoveCardsMutation({
          variables: {
            cardIds,
            idBoard,
            pos: pos.toString(),
            targetBoardId,
            targetListId,
            traceId,
          },
        });

        Analytics.taskSucceeded({
          taskName: 'edit-card-bulk/idBoard',
          source: source || 'moveCardInlineDialog',
          traceId,
        });
      } catch (error) {
        if (traceId) {
          Analytics.taskFailed({
            taskName: 'edit-card-bulk/idBoard',
            source: source || 'moveCardInlineDialog',
            traceId,
            error,
          });
        }
        throw error;
      }
    },
    [bulkMoveCardsMutation],
  );

  return { bulkMoveCards };
}
